/**
 * Audio-source discovery (F1 host capture).
 *
 * Two source families feed the uplink:
 *  - `mic`: a microphone chosen from `enumerateDevices` (getUserMedia).
 *  - `tab`: a browser tab / window / screen with its audio, via
 *    `getDisplayMedia({ audio: true })` (RF03 — capture meeting/tab audio).
 *
 * Device labels are empty until the page has been granted microphone
 * permission at least once, so the UI prompts for permission before listing.
 */

/** The kind of audio source the host is capturing. */
export type CaptureSourceKind = 'mic' | 'tab';

/** A selectable microphone input. */
export interface AudioInputDevice {
  deviceId: string;
  label: string;
}

/** A selectable audio output (speaker/headset), for TTS playback routing (F3). */
export interface AudioOutputDevice {
  deviceId: string;
  label: string;
}

/** Discriminated selection the capture controller consumes. */
export type CaptureSource =
  | { kind: 'mic'; deviceId: string | null }
  | { kind: 'tab' };

/** Human-readable failure surfaced to the UI. */
export class PermissionDeniedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PermissionDeniedError';
  }
}

/** True when the browser can capture tab/screen audio. */
export function supportsDisplayMediaAudio(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  );
}

/** True when the browser exposes microphone enumeration/capture. */
export function supportsUserMedia(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

/**
 * Prompt for microphone permission. Resolves silently on grant; throws
 * PermissionDeniedError with a clear message on denial or absence of a device.
 * The granted stream is stopped immediately — this call only unlocks labels.
 */
export async function ensureMicrophonePermission(): Promise<void> {
  if (!supportsUserMedia()) {
    throw new PermissionDeniedError(
      'Este navegador não expõe captura de microfone (mediaDevices indisponível).',
    );
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
  } catch (err) {
    throw toPermissionError(err);
  }
}

/**
 * Enumerate microphone inputs. Call `ensureMicrophonePermission` first so the
 * returned devices carry human-readable labels (browsers hide labels until a
 * capture permission has been granted).
 */
export async function listAudioInputDevices(): Promise<AudioInputDevice[]> {
  if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') {
    return [];
  }
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) => d.kind === 'audioinput')
    .map((d, i) => ({
      deviceId: d.deviceId,
      label: d.label || `Microfone ${i + 1}`,
    }));
}

/**
 * Enumerate audio OUTPUT devices (speakers/headsets) for TTS playback routing
 * (F3). Labels populate only after a capture permission has been granted, same
 * as inputs. Returns [] on browsers that do not expose `enumerateDevices`
 * (routing then falls back to the system default output — see TtsPlayer).
 */
export async function listAudioOutputDevices(): Promise<AudioOutputDevice[]> {
  if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') {
    return [];
  }
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) => d.kind === 'audiooutput')
    .map((d, i) => ({
      deviceId: d.deviceId,
      label: d.label || `Saída ${i + 1}`,
    }));
}

/** Normalize a getUserMedia/getDisplayMedia rejection into a friendly error. */
export function toPermissionError(err: unknown): PermissionDeniedError {
  const name = err instanceof DOMException ? err.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new PermissionDeniedError(
        'Permissão de captura negada. Autorize o acesso ao áudio no navegador e tente novamente.',
      );
    case 'NotFoundError':
    case 'OverconstrainedError':
      return new PermissionDeniedError(
        'Nenhum dispositivo de áudio compatível foi encontrado.',
      );
    case 'NotReadableError':
      return new PermissionDeniedError(
        'O dispositivo de áudio está em uso por outro aplicativo.',
      );
    default:
      return new PermissionDeniedError(
        'Não foi possível iniciar a captura de áudio.',
      );
  }
}
